import React from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { ClipboardCheck, ArrowRight, AlertTriangle, Award, Clock } from "lucide-react";

export default function PracticeExamIntroPage() {
  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-14" data-testid="practice-exam-intro">
        <div className="overline mb-3">Full mock exam</div>
        <h1 className="font-serif text-5xl text-[var(--dw-navy)] mb-4 tracking-tight leading-tight">
          The 100-question<br />practice exam.
        </h1>
        <p className="text-neutral-700 leading-relaxed max-w-2xl mb-10">
          Questions sampled by the NMLS blueprint weighting: 24% federal law, 27% MLO
          activities, 20% general knowledge, 18% ethics, 11% state content. Choose your mode below.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <InfoCard icon={ClipboardCheck} n="100" l="Questions" sub="Sampled by weight" />
          <InfoCard icon={Award} n="75%" l="Passing" sub="Score to pass" />
          <InfoCard icon={Clock} n="90 min" l="Timer (opt.)" sub="Real NMLS length" />
        </div>

        {/* Two modes */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <Link
            to="/practice-exam/take"
            data-testid="start-untimed"
            className="dw-card group p-6 hover:border-[var(--dw-gold)]"
          >
            <div className="overline mb-2">Study mode</div>
            <div className="font-serif text-2xl text-[var(--dw-navy)] mb-3 leading-tight">Untimed exam</div>
            <p className="text-sm text-neutral-600 leading-relaxed mb-4">
              Take your time. Ask the AI tutor on any question. Review each answer at your own pace.
            </p>
            <div className="text-sm font-medium text-[var(--dw-navy)] flex items-center gap-1 group-hover:gap-2 transition-all">
              Start untimed <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </div>
          </Link>

          <Link
            to="/practice-exam/take?timed=1"
            data-testid="start-timed"
            className="dw-card group p-6 relative overflow-hidden bg-[var(--dw-navy)] text-[var(--dw-parchment)] border-[var(--dw-navy)] hover:border-[var(--dw-gold)]"
          >
            <div className="dw-grid-lines absolute inset-0 opacity-10" />
            <div className="relative">
              <div className="overline mb-2 text-[var(--dw-gold)]">Simulation mode</div>
              <div className="font-serif text-2xl mb-3 leading-tight flex items-center gap-2">
                <Clock className="w-5 h-5 text-[var(--dw-gold)]" strokeWidth={1.5} /> Timed exam
              </div>
              <p className="text-sm text-white/80 leading-relaxed mb-4">
                90-minute countdown. Auto-submits when time runs out. This is how the real NMLS sitting feels.
              </p>
              <div className="text-sm font-medium text-[var(--dw-gold)] flex items-center gap-1 group-hover:gap-2 transition-all">
                Start timed exam <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </div>
            </div>
          </Link>
        </div>

        <div className="dw-card p-6">
          <div className="overline mb-2">Before you begin</div>
          <ul className="text-sm text-neutral-700 space-y-2 leading-relaxed">
            <li>· Answers graded immediately with a full breakdown by module.</li>
            <li>· You may skip questions and return via the navigator.</li>
            <li>· AI tutor is available on every question if you need to look something up.</li>
            <li>· Missed questions are automatically saved to your review drill.</li>
          </ul>
        </div>
      </div>
    </AppLayout>
  );
}

function InfoCard({ icon: Icon, n, l, sub }) {
  return (
    <div className="dw-card p-5">
      <Icon className="w-5 h-5 text-[var(--dw-gold-soft)] mb-3" strokeWidth={1.5} />
      <div className="font-serif text-3xl text-[var(--dw-navy)] leading-none mb-1">{n}</div>
      <div className="overline mb-1">{l}</div>
      <div className="text-xs text-neutral-500">{sub}</div>
    </div>
  );
}
